/**
 * OS TOKENS DE COR DO ALFREDO CENTRAL — "a tela de LCD".
 *
 * Fonte: `~/Projetos/alfredo-site/docs/sistema-de-design.md`, o sistema de
 * design do ecossistema, derivado da ANATOMIA DO ÍCONE de cada produto: um
 * ladrilho colorido, um disco preto (a "tela") e um rosto aceso. A regra do
 * dono é que a tela do produto se pareça com o ícone dele.
 *
 * O Central é o ladrilho AMARELO com o rosto AZUL.
 *
 * O QUE ESTE ARQUIVO CONSERTA
 *
 * Os dois temas do Central tinham os pigmentos certos e os PAPÉIS trocados:
 *
 *  · o amarelo `#fee97b` era `modalColor`, usado como SUPERFÍCIE grande. Sobre
 *    amarelo cheio só o carvão passa — e o que estava por cima era o azul claro;
 *  · o tema claro usava `#88DBFE` como cor de AÇÃO. Esse azul é o BRILHO do
 *    rosto, não a tinta: em papel ele não sustenta texto (é a razão de o
 *    documento separar `--aceso` de `--tinta`);
 *  · o tema escuro tinha `background: #8a8879` (um cinza-oliva) e
 *    `text: #000000`. Não era um tema escuro: era o claro com os cartões
 *    apagados — e foi assim que dois botões desta tela chegaram a 1,02:1 de
 *    contraste, medidos no navegador.
 *
 * A IDEIA QUE SUSTENTA OS DOIS TEMAS (seção 5.1 do documento): *a tela não muda
 * de cor; muda de tamanho.* `--tela` é `#252525` nos dois. O que o tema decide é
 * QUANTA área da página é tela — no escuro, quase tudo; no claro, umas poucas
 * ilhas (cabeçalho, painel do número grande, botão primário, selo de vazio),
 * com o resto em papel.
 *
 * AS TRÊS PROIBIÇÕES, que vieram da conta de contraste do documento:
 *
 *  1. o ladrilho é SEMPRE borda, moldura ou seleção — nunca tinta, nunca área
 *     grande no papel, nunca semântica;
 *  2. sobre ladrilho cheio, o único texto é `--carvao`;
 *  3. `--aceso` (o brilho) não vira texto em papel; quem faz esse trabalho é
 *     `--tinta`.
 *
 * FORMA: hex, e não o tripleto "R G B" do documento. O tripleto existe lá para
 * o Tailwind compor opacidade (`bg-ladrilho/15`); o Central é
 * styled-components, onde `+ "cc"` já é o idioma da casa para opacidade.
 */

/** Estrutura — IGUAL nos dois temas e nos cinco produtos. Sai do disco. */
export const ESTRUTURA = {
  /** A face do instrumento: cabeçalho, painel de número, botão primário, selo. */
  tela: "#252525",
  /** Hover e elevação sobre a tela; fundo de campo no tema escuro. */
  telaAlta: "#3F4040",
  /** Fundo de PÁGINA no tema escuro. */
  telaFundo: "#121212",
  /** Scrim de modal e sombra. */
  void: "#000000",
  telaTexto: "#EDEDED",
  telaTexto2: "#AFAFAF",
  telaTexto3: "#8F8F8F",
  /** Separador decorativo — isento do 1.4.11 por não carregar informação. */
  telaLinha: "#3A3A3A",
  /** Contorno de CONTROLE sobre a tela. Esse precisa passar em 1.4.11. */
  telaLinhaForte: "#737373",
  /** Texto do papel — e o único que pode pisar no ladrilho cheio. */
  carvao: "#191919",
  carvao2: "#5A5A5A",
  carvao3: "#6B6B6B",
  linha: "#E6E6E6",
  linhaForte: "#878787",
} as const;

/** Os sete tokens que mudam por produto. Estes são os do CENTRAL. */
export const CENTRAL = {
  /** O amarelo do ladrilho do ícone. Moldura e seleção — nunca tinta. */
  ladrilho: "#FEE97B",
  /** O amarelo que sobrevive ao papel (3:1 contra o creme da página). */
  ladrilhoBorda: "#AA8E01",
  /** O azul claro do rosto: brilho, destaque sobre a tela. */
  aceso: "#88DBFE",
  /** O azul cheio do rosto. */
  acesoCorpo: "#0B8DE9",
  /**
   * A cor de LINK e AÇÃO no papel.
   *
   * O alvo NÃO é o creme da página, e sim o LADRILHO — o amarelo cheio do
   * cabeçalho de tabela, do painel e do topo do modal. É lá que a tinta tem o
   * pior fundo, e era lá que ela reprovava: medido no navegador, 25 elementos
   * (os 18 cabeçalhos de coluna e os 7 títulos de seção do modal) ficavam em
   * 3,90:1 e 4,04:1, abaixo dos 4,5:1 que texto normal exige.
   *
   * Escurecida até passar no PIOR caso, ela passa em todos:
   *
   *     ladrilho #FEE97B   4,51:1     amarelo cheio, o pior fundo
   *     seção    #FEED93   4,66:1
   *     creme    #FEFADA   5,23:1     a página
   *     lavado   #FFFBEC   5,32:1     linha selecionada
   *     papel    #FFFEFB   5,47:1     a tabela
   *     branco sobre ela   5,52:1     o botão primário
   */
  tinta: "#086CB3",
  /** Branco tingido de amarelo a 3% — o Central é creme, não branco. */
  papel: "#FFFEFB",
  /**
   * O CREME DA PÁGINA — e aqui o Central se afasta da dose do documento, de
   * propósito.
   *
   * A tabela do sistema de design dá `--papel-2` = `#FFFEF7`, que é a fórmula
   * "branco + ladrilho a 6%". Na tela isso simplesmente lê como BRANCO: 6% de
   * um amarelo já claro não sobrevive ao monitor. O Central que existia antes
   * do sistema usava `#FEFADA` (a mesma mistura a ~25%), e é esse creme que o
   * dono reconhece como a cara do produto — a pedido dele, 31/08/2026, ele
   * volta.
   *
   * A intenção do documento (seção "o papel é tingido pelo ladrilho: não é
   * branco, é o branco DAQUELE produto") é respeitada; o que muda é a dose,
   * que ali é baixa demais para se enxergar.
   *
   * O CUSTO FOI MEDIDO, e pago: sobre `#FEFADA` a tinta caía para 4,47:1 e a
   * borda-ladrilho para 2,95:1 — os dois um fio abaixo do mínimo. Ambos foram
   * escurecidos o suficiente para voltar a passar (4,52:1 e 3,02:1), mexendo
   * 0,2% e 0,4% de luminosidade: invisível a olho, e a conta fecha.
   */
  papel2: "#FEFADA",
  /** Fundo de destaque; é o fundo da linha selecionada da tabela. */
  lavado: "#FFFBEC",
} as const;

/** Semânticas — trocam de valor entre os temas para manter o contraste. */
export const SEMANTICAS = {
  claro: { bom: "#2D7944", aviso: "#9A5C00", erro: "#CB2D23" },
  escuro: { bom: "#7BD69A", aviso: "#F0B65C", erro: "#F5837A" },
} as const;

/**
 * O bloco `:root` com todos os tokens, interpolado dentro do GlobalStyles
 * (junto de `raizEscala`). Um estilo global só, sem pergunta de ordem de
 * injeção — a mesma razão registrada em `escala.ts`.
 *
 * As semânticas entram na variante CLARA aqui; o tema escuro as sobrescreve
 * mais abaixo, por `[data-tema="dark"]`.
 */
export const raizDeCores = `
:root {
  --tela: ${ESTRUTURA.tela};
  --tela-alta: ${ESTRUTURA.telaAlta};
  --tela-fundo: ${ESTRUTURA.telaFundo};
  --void: ${ESTRUTURA.void};
  --tela-texto: ${ESTRUTURA.telaTexto};
  --tela-texto-2: ${ESTRUTURA.telaTexto2};
  --tela-texto-3: ${ESTRUTURA.telaTexto3};
  --tela-linha: ${ESTRUTURA.telaLinha};
  --tela-linha-forte: ${ESTRUTURA.telaLinhaForte};
  --carvao: ${ESTRUTURA.carvao};
  --carvao-2: ${ESTRUTURA.carvao2};
  --carvao-3: ${ESTRUTURA.carvao3};
  --linha: ${ESTRUTURA.linha};
  --linha-forte: ${ESTRUTURA.linhaForte};

  --ladrilho: ${CENTRAL.ladrilho};
  --ladrilho-borda: ${CENTRAL.ladrilhoBorda};
  --aceso: ${CENTRAL.aceso};
  --aceso-corpo: ${CENTRAL.acesoCorpo};
  --tinta: ${CENTRAL.tinta};
  --papel: ${CENTRAL.papel};
  --papel-2: ${CENTRAL.papel2};
  --lavado: ${CENTRAL.lavado};

  --bom: ${SEMANTICAS.claro.bom};
  --aviso: ${SEMANTICAS.claro.aviso};
  --erro: ${SEMANTICAS.claro.erro};

  /* O fio de 1px no topo de toda peça de tela. */
  --reflexo: rgba(255, 255, 255, 0.12);
}

[data-tema="dark"] {
  --bom: ${SEMANTICAS.escuro.bom};
  --aviso: ${SEMANTICAS.escuro.aviso};
  --erro: ${SEMANTICAS.escuro.erro};
}
`;

/**
 * O VERDE E O ÂMBAR DO BOOTSTRAP, TRADUZIDOS PARA OS QUE SE LEEM.
 *
 * Espalhados pela árvore em TSX como literal (`$color="#28a745"`), eles vêm da
 * paleta padrão do Bootstrap — feita para texto BRANCO por cima, não para ser
 * o texto. Medido no navegador, sobre o creme da página:
 *
 *     #28a745  2,97:1   o "3" de Lojas Ativas
 *     #ffc107  1,54:1   o "1" de Ambientes
 *
 * Mesmo com a folga de texto grande (3:1) os dois reprovam, e o âmbar reprova
 * por muito. Esta função troca só esses dois pela semântica do sistema de
 * design, que existe exatamente para isso — `--bom` e `--aviso`, calibrados
 * contra o papel. Qualquer outra cor passa direto.
 *
 * É um mapa e não uma busca-e-substitui nos TSX porque o MESMO literal também é
 * usado como fundo e como borda a 8%–50% de opacidade, onde ele está correto:
 * trocar tudo mudaria o desenho de cartão que não tem defeito nenhum.
 */
export function corDeTextoAcessivel(cor: string | undefined): string | undefined {
  if (!cor) return cor;
  const mapa: Record<string, string> = {
    "#28a745": SEMANTICAS.claro.bom,
    "#28A745": SEMANTICAS.claro.bom,
    "#ffc107": SEMANTICAS.claro.aviso,
    "#FFC107": SEMANTICAS.claro.aviso,
    "#dc3545": SEMANTICAS.claro.erro,
    "#DC3545": SEMANTICAS.claro.erro,
  };
  return mapa[cor] ?? cor;
}
